/**
 * HeyDex's side of the app's Settings: inside the HeyDay News app, HeyDex has no
 * settings screen, and the app's one Settings screen reads and changes the reader's
 * HeyDex settings through the bridge (`@heyday/news-api/heydex`). HeyDex reports them
 * once loaded and after every change, and applies each of the app's changes through its
 * own settings client, answering it by id once its save has settled.
 */
import {
  AUTO_SETTLE_DAYS,
  type HeyDexReasoning,
  type HeyDexSettings,
} from "@heyday/news-api/heydex";
import {
  type ClientSettings,
  type ModelSelection,
  ProviderInstanceId,
  type ServerSettings,
} from "@heydex/contracts";

/** The option on a hosted model selection that holds the reasoning level. */
const REASONING_OPTION_ID = "effort";

/** A hosted server's one model; a reader with no saved default starts from it. */
const HOSTED_SELECTION: ModelSelection = {
  instanceId: ProviderInstanceId.make("heydex"),
  model: "heydex",
};

export type HeyDexSettingsSource = Pick<
  ServerSettings,
  | "defaultModelSelection"
  | "responseStreamingMode"
  | "sidebarAutoSettleAfterDays"
  | "snoozeLimitedThreads"
  | "autoResumeLimitedThreads"
> &
  Pick<ClientSettings, "inAppNotificationsEnabled">;

/** The level a selection names; a hosted selection without one runs at Medium. */
export function reasoningOf(selection: ModelSelection | null): HeyDexReasoning {
  const value = selection?.options?.find((option) => option.id === REASONING_OPTION_ID)?.value;
  return value === "low" || value === "high" ? value : "medium";
}

function withReasoning(selection: ModelSelection, level: HeyDexReasoning): ModelSelection {
  return {
    ...selection,
    options: [
      ...(selection.options ?? []).filter((option) => option.id !== REASONING_OPTION_ID),
      { id: REASONING_OPTION_ID, value: level },
    ],
  };
}

export function heydexSettingsFrom(settings: HeyDexSettingsSource): HeyDexSettings {
  return {
    defaultReasoning: reasoningOf(settings.defaultModelSelection),
    responseStreamingMode: settings.responseStreamingMode,
    // The app takes whole days; HeyDex's own settings allow a fraction.
    sidebarAutoSettleAfterDays:
      settings.sidebarAutoSettleAfterDays === null
        ? null
        : Math.min(
            AUTO_SETTLE_DAYS.max,
            Math.max(AUTO_SETTLE_DAYS.min, Math.round(settings.sidebarAutoSettleAfterDays)),
          ),
    snoozeLimitedThreads: settings.snoozeLimitedThreads,
    autoResumeLimitedThreads: settings.autoResumeLimitedThreads,
    inAppNotificationsEnabled: settings.inAppNotificationsEnabled,
  };
}

/** The settings a change from the app leaves: the change when it saved, else what stood. */
export function settledSettings(
  current: HeyDexSettings,
  change: Partial<HeyDexSettings>,
  saved: boolean,
): HeyDexSettings {
  return saved ? { ...current, ...change } : current;
}

/** The settings patch a change from the app asks for. */
export function settingsPatchFor(
  change: Partial<HeyDexSettings>,
  defaultModelSelection: ModelSelection | null,
): Partial<HeyDexSettingsSource> {
  const { defaultReasoning, ...rest } = change;
  return {
    ...rest,
    ...(defaultReasoning === undefined
      ? {}
      : {
          defaultModelSelection: withReasoning(
            defaultModelSelection ?? HOSTED_SELECTION,
            defaultReasoning,
          ),
        }),
  };
}
